import { Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import StyledWhiteBox from 'src/components/common/StyledWhiteBox';
import { RankByDataPackage } from 'src/features/AnalyticsPackage/Common/config';
import { useQueryParams } from 'src/hooks/useQueryParam';
import CustomTable from 'src/components/Table';
import { TypeDataPackage } from 'src/utils/constants/enum';
import { ITypeCompanyDataPackage } from 'src/utils/typings/response';
import { getArrayDefault, swapElements } from 'src/utils/helpers/arrayHelpers';
import { useQueryListDataPackage } from 'src/services/queries/useQueryTradeData';
import FlagCountry from 'src/components/common/FlagCountry';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { renderTextOrDefault, renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';

const CompanyDataPackage = () => {
  const theme = useTheme();
  const { selectColumns: selectColumnParam } = useQueryParams();
  const [params, setParams] = useState({
    page: 0,
    page_size: 25,
  });
  const {
    rank_by = RankByDataPackage.NumberBol,
    type_package = TypeDataPackage.top_20_export_company_in_country,
    trade_data_from = '',
    trade_data_to = '',
    country = '',
    is_worldwide,
  } = useQueryParams();
  const [selectedColumn, setSelectedColumn] = useState<string[]>([]);
  const isWorldwide = useMemo(() => is_worldwide === 'true', [is_worldwide]);

  const { data, isFetching } = useQueryListDataPackage({
    type_package: String(type_package) as TypeDataPackage,
    trade_data_from: String(trade_data_from),
    trade_data_to: String(trade_data_to),
    country: String(country),
    rank_by: String(rank_by) as RankByDataPackage,
    is_worldwide: isWorldwide,
  });

  const listCompanies = getArrayDefault(data?.data);

  const listCompaniesPaginate = useMemo(() => {
    return listCompanies.slice(params.page * params.page_size, (params.page + 1) * params.page_size);
  }, [params, listCompanies]);

  const configColumn: ConfigCell<ITypeCompanyDataPackage>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: 'Company',
      cell: (data: ITypeCompanyDataPackage) => (
        <RouterLinkNoUnderline target="_blank" to={AuthRouteKeys.CompanyDetail.replace(':id?', data.company_id || '')}>
          <Stack spacing={1} direction="row">
            <FlagCountry width={24} code={String(data.company_country_code) || ''} />
            <Typography component={'span'} variant="body2" color="secondary.main">
              {renderTextOrDefault(data.company_name)}
            </Typography>
          </Stack>
        </RouterLinkNoUnderline>
      ),
    },
    {
      disablePadding: false,
      id: 'company_name',
      label: 'No of HS Code',
      cell: (data: ITypeCompanyDataPackage) => (
        <Stack spacing={1} direction="row">
          <Typography component={'span'} noWrap variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.hs_codes)}
          </Typography>
        </Stack>
      ),
    },
  ];

  const configColumnRankBy: ConfigCell<ITypeCompanyDataPackage>[] = [
    {
      disablePadding: false,
      id: 'bol',
      label: 'No of B/L',
      cell: (data: ITypeCompanyDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight',
      cell: (data: ITypeCompanyDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ITypeCompanyDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: 'Value (USD)',
      cell: (data: ITypeCompanyDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.value_usd)}
        </Typography>
      ),
    },
  ];

  const sortedConfigRankBy = useMemo(() => {
    const indexRankBy = configColumnRankBy.findIndex((config) => config.id === rank_by);
    return swapElements(configColumnRankBy, 0, indexRankBy).filter((config) => selectedColumn.includes(config.id));
  }, [rank_by, selectedColumn]);

  useEffect(() => {
    /** Get Init value of selectedColumns state */
    const selectColumns = selectColumnParam
      ? String(selectColumnParam).split(',')
      : configColumnRankBy.map((item) => item.id);
    setSelectedColumn(selectColumns);
  }, []);

  return (
    <StyledWhiteBox p={theme.spacing(3)}>
      <Stack spacing={3} justifyContent={'space-between'} alignItems={'center'} direction={'row'} mb={3}>
        <Typography variant="h5">Companies List</Typography>
      </Stack>
      <CustomTable
        page={params.page}
        pageSize={params.page_size}
        onChangePage={(e) => setParams({ ...params, ...{ page: e } })}
        onChangePageSize={(e) => setParams({ ...params, ...{ page: 0, page_size: e } })}
        totalRecords={listCompanies.length}
        loading={isFetching}
        data={listCompaniesPaginate}
        dataKey="company_name"
        configColumn={[...configColumn, ...sortedConfigRankBy]}
      />
    </StyledWhiteBox>
  );
};

export default CompanyDataPackage;
